import { appEnv } from './env';

export class ApiError extends Error {
  readonly status: number;
  readonly payload: unknown;

  constructor(message: string, status: number, payload: unknown = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
  }
}

export type ApiFetchOptions = RequestInit & {
  /** Forward the stored upstream bearer, when there is one. Default true. */
  authenticated?: boolean;
  /** Abort after this many ms. Omit for no client-side limit. */
  timeoutMs?: number;
};

function buildUrl(baseUrl: string, path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${normalizedPath}`;
}

/**
 * Which credential this request carries.
 *
 * The Identity Studio access token when there is one — that is the session this
 * service verifies. Otherwise the platform bearer, which is not a session at all
 * but is what a deployment without IAM forwards for data reads. Deliberately in
 * that order: once a real session exists it is the one that should be presented,
 * and a stale platform token must not shadow it.
 */
/**
 * ⭐ `vaarta_token` MEANS TWO DIFFERENT THINGS, and that is the collision (AG-1583 item 4).
 *
 * Here it is the *platform bearer* — not a session, the credential a deployment without
 * IAM forwards for data reads. In Automation Studio the identical key is that app's own
 * *session* token (`vaarta-frontend/src/lib/env.ts` defaults `authTokenStorageKey` to it).
 *
 * Harmless today only because the two live on separate origins. Under AG-1586, which
 * serves auth on the application's own domain, they become one storage slot: Analytics
 * would present Automation Studio's HS256 session to this API, which verifies RS256 via
 * JWKS and pins the `app` claim, so it refuses it — a broken session rather than a
 * privilege escalation, but broken in a way that reads as "sign-in is down".
 *
 * The cure is to rename the thing that is NOT a session, since renaming a live session key
 * signs everybody out. `PLATFORM_BEARER_KEY` is that name and is preferred; the legacy key
 * stays readable because nothing in this repository writes either one — an operator or an
 * older build populates it — so dropping it would break whichever deployments rely on it,
 * and squashed history offers no way to find out which.
 */
const PLATFORM_BEARER_KEY = 'vaarta_platform_bearer';
const LEGACY_PLATFORM_BEARER_KEY = 'vaarta_token';

function storedToken(): string | null {
  try {
    return (
      localStorage.getItem('analytics_access_token') ||
      localStorage.getItem(appEnv.authTokenStorageKey) ||
      localStorage.getItem(PLATFORM_BEARER_KEY) ||
      (appEnv.authTokenStorageKey === LEGACY_PLATFORM_BEARER_KEY
        ? null
        : localStorage.getItem(LEGACY_PLATFORM_BEARER_KEY))
    );
  } catch {
    return null;
  }
}

/**
 * What happens when the server rejects our session.
 *
 * The transport reports; the session owner decides. This is registered by
 * `useAuth`, which tries a refresh once and signs out if that fails — policy
 * that belongs with the session, not in the network layer, which is where it
 * used to live and why a 401 anywhere could navigate the whole page.
 */
type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler | null = null;

export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  onUnauthorized = handler;
}

/**
 * What happens when a SIBLING application refuses to be read for this person.
 *
 * The same split as above, and for the same reason: the transport reports, and
 * something that knows about sessions decides. A 424 is the backend saying "we
 * are sure who you are, and Data Studio still would not let us act for you" —
 * and the interesting half is whether the person can do anything about it. That
 * is a judgement about sign-in, so it belongs with the session (see
 * `AccountMismatchGate`), not here.
 *
 * Reported for EVERY 424, including the ones `fetchApiSources` swallows into its
 * outage list. A refusal that only ever reached the sources screen is invisible
 * to somebody who is uploading a spreadsheet, which is where it was last seen.
 */
type SiblingRefusalHandler = (error: ApiError) => void;
let onSiblingRefusal: SiblingRefusalHandler | null = null;

export function setSiblingRefusalHandler(handler: SiblingRefusalHandler | null): void {
  onSiblingRefusal = handler;
}

function extractErrorMessage(payload: unknown, fallback: string): string {
  if (typeof payload === 'string' && payload.trim()) return payload;
  if (!payload || typeof payload !== 'object') return fallback;

  const row = payload as Record<string, unknown>;
  const detail = row.detail;

  if (typeof row.message === 'string' && row.message.trim()) return row.message;
  if (typeof row.error === 'string' && row.error.trim()) return row.error;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (detail && typeof detail === 'object') {
    const detailRow = detail as Record<string, unknown>;
    if (typeof detailRow.message === 'string' && detailRow.message.trim()) return detailRow.message;
    if (typeof detailRow.error === 'string' && detailRow.error.trim()) return detailRow.error;
  }
  // FastAPI validation errors arrive as `detail: [{loc, msg, type}]`.
  if (Array.isArray(detail)) {
    const first = detail.find((d) => d && typeof d === 'object' && 'msg' in d) as { msg?: string } | undefined;
    if (first?.msg) return first.msg;
  }

  return fallback;
}

async function parseResponseBody(response: Response): Promise<unknown> {
  if (response.status === 204) return null;
  const text = await response.text().catch(() => '');
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

async function request<T>(baseUrl: string, path: string, init: ApiFetchOptions): Promise<T> {
  const headers = new Headers(init.headers || {});
  const token = storedToken();
  const shouldAuthenticate = init.authenticated !== false;

  if (!headers.has('Accept')) headers.set('Accept', 'application/json');
  if (shouldAuthenticate && token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  if (
    init.body !== undefined &&
    init.body !== null &&
    !(init.body instanceof FormData) &&
    !headers.has('Content-Type')
  ) {
    headers.set('Content-Type', 'application/json');
  }

  const { authenticated: _authenticated, timeoutMs, signal, ...requestInit } = init;
  const timeoutSignal = timeoutMs ? AbortSignal.timeout(timeoutMs) : undefined;
  const composedSignal =
    signal && timeoutSignal ? AbortSignal.any([signal, timeoutSignal]) : (signal ?? timeoutSignal);

  try {
    const response = await fetch(buildUrl(baseUrl, path), {
      ...requestInit,
      headers,
      signal: composedSignal ?? null,
    });
    const payload = await parseResponseBody(response);

    if (!response.ok) {
      /*
       * A 401 on a request that CARRIED a session means that session was
       * refused, and the session owner is told so it can refresh or sign out.
       * A 401 on a request that carried nothing means the far side wanted a
       * credential we never had — an upstream data-access problem, not a
       * rejected session, and reporting it as one would sign a person out over
       * a data source they were never entitled to read.
       */
      if (response.status === 401 && shouldAuthenticate && token) onUnauthorized?.();
      const apiError = new ApiError(
        extractErrorMessage(payload, `Request failed with status ${response.status}`),
        response.status,
        payload,
      );
      // Never a sign-out: 424 exists precisely BECAUSE this person's session is
      // valid. Reported so the session owner can offer the one fix that helps.
      if (response.status === 424) onSiblingRefusal?.(apiError);
      throw apiError;
    }
    return payload as T;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiError('The request was cancelled.', 0);
    }
    if (error instanceof DOMException && error.name === 'TimeoutError') {
      throw new ApiError('The request timed out.', 0);
    }
    const message = error instanceof Error ? error.message : 'Failed to fetch';
    throw new ApiError(message, 0);
  }
}

/** Calls the standalone charting backend (/v1/chart-engine/*). */
export function apiFetch<T>(path: string, init: ApiFetchOptions = {}): Promise<T> {
  return request<T>(appEnv.chartApiBaseUrl, path, init);
}

/** Calls the Vaarta platform (api registry, data-studio, auth) over HTTP. */
export function vaartaFetch<T>(path: string, init: ApiFetchOptions = {}): Promise<T> {
  return request<T>(appEnv.vaartaApiBaseUrl, path, init);
}
